import React, { useState } from 'react';
import { DollarSign, Flame, CreditCard, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { donationInfo } from '../mock';
import { useTranslation } from '../i18n/LanguageContext';

const GivePage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copiedVenmo, setCopiedVenmo] = useState(false);
  const [copiedZelle, setCopiedZelle] = useState(false);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'venmo') {
      setCopiedVenmo(true);
      setTimeout(() => setCopiedVenmo(false), 2000);
    } else {
      setCopiedZelle(true);
      setTimeout(() => setCopiedZelle(false), 2000);
    }
    toast({
      title: t('give.copiedToast'),
      description: type === 'venmo' ? t('give.copiedVenmoBody') : t('give.copiedZelleBody')
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="text-white py-24 md:py-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/images/holy-fire-bg.jpg')" }}></div>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8 fade-in">
            <Badge variant="secondary" className="bg-white/20 text-white border-white/30 uppercase tracking-[0.3em] text-xs">
              {t('give.badge')}
            </Badge>
            <h1 className="display-hero text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              {t('give.heroTitle')} <span className="italic font-light">{t('give.heroAccent')}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto">
              {t('give.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Giving Options */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Venmo */}
            <Card className="mb-8 card-hover border-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <Flame className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">{t('give.venmoTitle')}</CardTitle>
                      <CardDescription className="text-base">
                        {t('give.venmoDesc')}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('give.venmoHandle')}</p>
                    <p className="text-2xl font-bold">{donationInfo.venmo.handle}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(donationInfo.venmo.handle, 'venmo')}
                  >
                    {copiedVenmo ? (
                      <><Check className="h-4 w-4 mr-2" /> {t('give.copied')}</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" /> {t('give.copy')}</>
                    )}
                  </Button>
                </div>

                <div className="text-center p-6 bg-secondary rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    {t('give.qrSoonPrefix')} <strong>{donationInfo.venmo.handle}</strong> {t('give.qrSoonSuffix')}
                  </p>
                </div>

                <a
                  href={donationInfo.venmo.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full btn-hover" size="lg">
                    {t('give.openVenmo')}
                    <DollarSign className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Zelle */}
            <Card className="card-hover border-2">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                    <CreditCard className="h-8 w-8 text-accent" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{t('give.zelleTitle')}</CardTitle>
                    <CardDescription className="text-base">
                      {t('give.zelleDesc')}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground mb-1">{t('give.zelleSendTo')}</p>
                    <p className="text-lg font-bold break-all">{donationInfo.zelle.email}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(donationInfo.zelle.email, 'zelle')}
                    className="ml-4"
                  >
                    {copiedZelle ? (
                      <><Check className="h-4 w-4 mr-2" /> {t('give.copied')}</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" /> {t('give.copy')}</>
                    )}
                  </Button>
                </div>

                <div className="p-4 bg-accent/10 rounded-lg">
                  <h4 className="font-semibold mb-2">{t('give.zelleHowTitle')}</h4>
                  <ol className="text-sm text-muted-foreground space-y-2">
                    <li>1. {t('give.zelleStep1')}</li>
                    <li>2. {t('give.zelleStep2')}</li>
                    <li>3. {t('give.zelleStep3')} <strong className="text-foreground">{donationInfo.zelle.email}</strong></li>
                    <li>4. {t('give.zelleStep4')}</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-24 md:py-32 section-gradient">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
              {t('give.thankYouTitle')} <span className="italic font-light text-gradient">{t('give.thankYouAccent')}</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t('give.thankYouBody')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GivePage;
